import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spinner, initAnalytics, resolveAnalytics } from '@easydent/shared-ui';
import AppCookieBanner from '@/components/AppCookieBanner';
import { Footer } from '@/components/SiteChrome';

// Platform entry point + public clinic directory
const LandingPage = React.lazy(() => import('@/pages/LandingPage'));
const DirectoryPage = React.lazy(() => import('@/pages/DirectoryPage'));
const ClinicProfilePage = React.lazy(() => import('@/pages/ClinicProfilePage'));
const BookingPage = React.lazy(() => import('@/pages/BookingPage'));
const ForClinicsPage = React.lazy(() => import('@/pages/ForClinicsPage'));
const ForImagingCentresPage = React.lazy(() => import('@/pages/ForImagingCentresPage'));
const ForSuppliersPage = React.lazy(() => import('@/pages/ForSuppliersPage'));
const ForPatientsPage = React.lazy(() => import('@/pages/ForPatientsPage'));
const FeaturesPage = React.lazy(() => import('@/pages/FeaturesPage'));
const FeatureDetailPage = React.lazy(() => import('@/pages/FeatureDetailPage'));
const LegalPage = React.lazy(() => import('@/pages/LegalPage'));
const ContactPage = React.lazy(() => import('@/pages/ContactPage'));

const PageLoader = () => (
  <div className="flex h-screen items-center justify-center">
    <Spinner size="lg" />
  </div>
);

const App: React.FC = () => {
  // Consent-gated analytics. Stays off until this app has a website id AND the visitor consents.
  useEffect(() => {
    initAnalytics(resolveAnalytics('public'));
  }, []);

  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/directory" element={<DirectoryPage />} />
          <Route path="/for-clinics" element={<ForClinicsPage />} />
          <Route path="/for-imaging-centres" element={<ForImagingCentresPage />} />
          <Route path="/for-suppliers" element={<ForSuppliersPage />} />
          <Route path="/for-patients" element={<ForPatientsPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/features/:slug" element={<FeatureDetailPage />} />
          <Route path="/legal/:slug" element={<LegalPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/clinic/:slug" element={<ClinicProfilePage />} />
          <Route path="/clinic/:slug/book" element={<BookingPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      {/* Rendered here, not per page. It used to be each page's job to remember, and seven of ten
          did not — so terms, privacy and the cookie policy were unreachable from most of the site,
          including the anonymous booking page where a patient gives GDPR consent (#478). A layout
          element belongs to the layout, where a new page cannot forget it. */}
      <Footer />
      <AppCookieBanner />
    </>
  );
};

export default App;
